// Open positions across the fleet. Positions are fetched per-account (the accounts snapshot
// carries counts, not the position rows) for accounts that currently hold positions, then
// grouped/filtered/sorted. Currency P&L is never summed across currencies — group totals
// show one figure per currency. Row click opens the account inspector.

import { useEffect, useMemo, useRef } from 'react';
import * as api from '../api/client';
import type { AccountRow, Position } from '../types/api';
import { C, MONO } from '../lib/tokens';
import { fmtAge, fmtMoney, fmtTS, hbTitle } from '../lib/format';
import { toNum, sumDec } from '../lib/decimal';
import { CHIP_BASE, CHIP_ON, mTd, mTh, mono } from '../lib/styles';
import { derive, type Derived } from '../lib/mappers';
import { pnlColor, EmptyState } from '../components/common';
import { useData, useServerNow, useUi } from '../store/hooks';
import { openDrawer, uiStore } from '../store/uiStore';
import { useResource } from '../hooks/useResource';

interface Row {
  a: AccountRow;
  p: Position;
}

interface CachedPositions {
  seq: string;
  positions: Position[];
}

interface PositionEntry {
  accountId: string;
  p: Position;
}

interface PositionLoadResult {
  entries: PositionEntry[];
  failedAccountIds: string[];
}

type FetchOutcome =
  | {
      ok: true;
      id: string;
      seq: string;
      positions: Position[];
    }
  | {
      ok: false;
      id: string;
    };

const POSITION_FETCH_CONCURRENCY = 6;

async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) return [];
  const results = new Array<R>(items.length);
  let next = 0;

  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await mapper(items[index]!);
    }
  }

  const workers = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

function priceStr(s: string | null | undefined, digits: number): string {
  const n = toNum(s);
  return n == null ? '—' : n.toFixed(digits);
}

/** Format a currency→amount map like "$1,200.00 + €300.00". */
function fmtCcyMap(map: Map<string, string>, signed = true): string {
  const parts: string[] = [];
  for (const [ccy, amt] of map) parts.push(fmtMoney(amt, ccy, signed));
  return parts.length ? parts.join('  ·  ') : fmtMoney('0', 'USD');
}

export function Positions() {
  const accounts = useData((s) => s.accounts);
  const thr = useUi((s) => s.thr);
  const q = useUi((s) => s.q);
  const posGroup = useUi((s) => s.posGroup);
  const posDir = useUi((s) => s.posDir);
  const serverNow = useServerNow();

  const withPositions = accounts.filter((a) => a.positions_count > 0);
  const positionCache = useRef(new Map<string, CachedPositions>());
  const key = withPositions
    .map((a) => `${a.id}:${a.portfolio_seq}`)
    .sort()
    .join(',');

  const res = useResource<PositionLoadResult>(
    key
      ? async (signal) => {
          const activeIds = new Set(withPositions.map((a) => a.id));
          for (const id of positionCache.current.keys()) {
            if (!activeIds.has(id)) positionCache.current.delete(id);
          }

          const changed = withPositions.filter(
            (a) => positionCache.current.get(a.id)?.seq !== a.portfolio_seq,
          );

          const fetched = await mapWithConcurrency<AccountRow, FetchOutcome>(
            changed,
            POSITION_FETCH_CONCURRENCY,
            async (a) => {
              try {
                const response = await api.accountPositions(a.id, signal);
                return {
                  ok: true,
                  id: a.id,
                  seq: a.portfolio_seq,
                  positions: response.positions,
                };
              } catch (error) {
                if (signal.aborted) throw error;
                return { ok: false, id: a.id };
              }
            },
          );
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          const failedAccountIds: string[] = [];
          for (const item of fetched) {
            if (item.ok) {
              positionCache.current.set(item.id, {
                seq: item.seq,
                positions: item.positions,
              });
            } else {
              failedAccountIds.push(item.id);
            }
          }

          return {
            entries: withPositions.flatMap((a) =>
              (positionCache.current.get(a.id)?.positions ?? []).map((p) => ({
                accountId: a.id,
                p,
              })),
            ),
            failedAccountIds,
          };
        }
      : async () => {
          positionCache.current.clear();
          return { entries: [], failedAccountIds: [] };
        },
    [key],
  );

  const failedAccountIds = res.data?.failedAccountIds ?? [];
  const hasLoadError = failedAccountIds.length > 0 || res.error !== null;
  const reloadRef = useRef(res.reload);
  reloadRef.current = res.reload;
  useEffect(() => {
    if (res.loading || !hasLoadError) return;
    const timer = window.setTimeout(() => reloadRef.current(), 5000);
    return () => window.clearTimeout(timer);
  }, [res.data, res.error, res.loading, hasLoadError]);

  const accountById = useMemo(
    () => new Map(accounts.map((account) => [account.id, account])),
    [accounts],
  );
  const rows: Row[] = (res.data?.entries ?? []).flatMap((entry) => {
    const account = accountById.get(entry.accountId);
    return account ? [{ a: account, p: entry.p }] : [];
  });
  const derivedMap = useMemo(() => {
    const m = new Map<string, Derived>();
    for (const a of accounts) m.set(a.id, derive(a, serverNow, thr));
    return m;
  }, [accounts, serverNow, thr]);

  const qNorm = q.trim().toLowerCase();
  const filtered = rows.filter(
    (r) =>
      (!posDir || r.p.side === posDir) &&
      (!qNorm ||
        (r.a.label + ' ' + r.a.broker + ' ' + r.p.symbol + ' ' + r.a.magic + ' ' + r.a.strategy + ' ' + r.p.ticket)
          .toLowerCase()
          .includes(qNorm)),
  );

  const keyFn: Record<string, (r: Row) => string> = {
    account: (r) => 'login ' + r.a.login_masked + ' · ' + r.a.broker,
    vps: (r) => r.a.vps_id,
    broker: (r) => r.a.broker,
    symbol: (r) => r.p.symbol,
    direction: (r) => (r.p.side === 'buy' ? 'Long' : 'Short'),
    strategy: (r) => r.a.strategy,
    magic: (r) => 'Magic ' + r.a.magic,
    none: () => 'All positions',
  };
  const kf = keyFn[posGroup] ?? keyFn.account!;
  const groups = new Map<string, Row[]>();
  for (const r of filtered) {
    const k = kf(r);
    const arr = groups.get(k) ?? [];
    arr.push(r);
    groups.set(k, arr);
  }

  const head = ['Account', 'Symbol', 'Ticket', 'Dir', 'Lots', 'Entry', 'Current', 'SL', 'TP', 'Float P&L', 'Swap', 'Age', 'Magic', 'Strategy', 'Fresh'];

  const buyLots = sumDec(filtered.filter((r) => r.p.side === 'buy').map((r) => r.p.volume));
  const sellLots = sumDec(filtered.filter((r) => r.p.side === 'sell').map((r) => r.p.volume));
  const netByCcy = new Map<string, string[]>();
  for (const r of filtered) {
    const arr = netByCcy.get(r.a.currency) ?? [];
    arr.push(r.p.profit, r.p.swap);
    netByCcy.set(r.a.currency, arr);
  }
  const netMap = new Map<string, string>();
  for (const [ccy, list] of netByCcy) netMap.set(ccy, sumDec(list));

  const posTotal =
    filtered.length +
    ' positions · ' +
    (toNum(buyLots) ?? 0).toFixed(2) +
    ' long / ' +
    (toNum(sellLots) ?? 0).toFixed(2) +
    ' short · net floating ' +
    fmtCcyMap(netMap);

  const groupSelOpts: Array<[string, string]> = [
    ['account', 'Group: account'],
    ['symbol', 'Group: symbol'],
    ['direction', 'Group: direction'],
    ['broker', 'Group: broker'],
    ['vps', 'Group: VPS'],
    ['strategy', 'Group: strategy'],
    ['magic', 'Group: magic'],
    ['none', 'No grouping'],
  ];

  return (
    <section data-screen-label="Open positions" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Open positions</h1>
        <span style={{ fontSize: 11, color: C.dm, fontFamily: MONO }}>{res.loading && !res.data ? 'Loading positions…' : posTotal}</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Direction filter">
          {([['', 'All'], ['buy', 'Long'], ['sell', 'Short']] as const).map(([v, label]) => (
            <button key={label} onClick={() => uiStore.set({ posDir: v })} aria-pressed={posDir === v} style={posDir === v ? CHIP_ON : CHIP_BASE}>
              {label}
            </button>
          ))}
        </div>
        <select
          value={posGroup}
          onChange={(e) => uiStore.set({ posGroup: e.target.value })}
          aria-label="Group positions by"
          style={{ background: '#121B2C', border: '1px solid ' + C.ln, color: C.tx, borderRadius: 5, height: 26, fontSize: 11.5, padding: '0 4px' }}
        >
          {groupSelOpts.map((o) => (
            <option key={o[0]} value={o[0]}>
              {o[1]}
            </option>
          ))}
        </select>
      </div>
      <div style={{ fontSize: 11, color: C.dm, maxWidth: 920 }}>
        Hedging accounts can hold buy and sell on the same symbol simultaneously; netting accounts show one net position per symbol. P&L is shown in each account's currency.
      </div>

      {hasLoadError && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 12px',
            border: '1px solid ' + C.am,
            borderRadius: 7,
            background: 'rgba(240, 173, 78, 0.08)',
            color: C.am,
            fontSize: 11.5,
          }}
        >
          <span style={{ flex: 1 }}>
            {failedAccountIds.length > 0
              ? `Could not refresh ${failedAccountIds.length} account${failedAccountIds.length === 1 ? '' : 's'}. Showing last known position data where available; retrying automatically.`
              : 'Position data could not be refreshed. Showing last known values where available; retrying automatically.'}
          </span>
          <button
            type="button"
            onClick={res.reload}
            disabled={res.loading}
            style={{ ...CHIP_BASE, color: C.am, borderColor: C.am }}
          >
            Retry now
          </button>
        </div>
      )}

      {Array.from(groups.keys())
        .sort()
        .map((gk) => {
          const rs = groups.get(gk)!.slice().sort((p1, p2) => (toNum(p1.p.profit) ?? 0) - (toNum(p2.p.profit) ?? 0));
          const lots = sumDec(rs.map((r) => r.p.volume));
          const gNet = new Map<string, string[]>();
          for (const r of rs) {
            const arr = gNet.get(r.a.currency) ?? [];
            arr.push(r.p.profit, r.p.swap);
            gNet.set(r.a.currency, arr);
          }
          const gNetMap = new Map<string, string>();
          for (const [ccy, list] of gNet) gNetMap.set(ccy, sumDec(list));
          return (
            <div key={gk} style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 14px', borderBottom: '1px solid ' + C.ln2 }}>
                <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>{gk}</h2>
                <span style={{ fontSize: 10.5, color: C.dm, fontFamily: MONO }}>
                  {rs.length + ' positions · ' + (toNum(lots) ?? 0).toFixed(2) + ' lots · ' + fmtCcyMap(gNetMap)}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1180 }}>
                  <thead>
                    <tr>
                      {head.map((h, i) => (
                        <th key={h} scope="col" style={mTh(i >= 4 && i <= 10 ? 'right' : 'left')}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rs.map((r) => {
                      const a = r.a;
                      const p = r.p;
                      const d = derivedMap.get(a.id)!;
                      const pl = toNum(p.profit);
                      const fresh = d.tr === 'inactive' ? '☾ closed' : fmtAge(d.qtAgeMs);
                      const fc = d.tr === 'inactive' ? C.gy : (d.qtAgeMs ?? 0) > 300000 ? C.rd : (d.qtAgeMs ?? 0) > 60000 ? C.am : C.dm;
                      return (
                        <tr key={a.id + ':' + p.position_id} onClick={() => openDrawer(a.id)} style={{ cursor: 'pointer' }}>
                          <td style={mTd('left', { fontWeight: 600 })} title={a.broker + ' · ' + a.margin_mode + ' account'}>{a.label}</td>
                          <td style={mTd('left', mono())}>{p.symbol}</td>
                          <td style={mTd('left', mono(C.dm))}>{p.ticket}</td>
                          <td style={mTd('left', mono(p.side === 'buy' ? C.bl : C.violet))} title={p.side === 'buy' ? 'long / buy' : 'short / sell'}>{p.side === 'buy' ? '▲ L' : '▼ S'}</td>
                          <td style={mTd('right', mono())}>{(toNum(p.volume) ?? 0).toFixed(2)}</td>
                          <td style={mTd('right', mono(C.b9))}>{priceStr(p.price_open, p.digits)}</td>
                          <td style={mTd('right', mono(C.b9))} title={'as of ' + fmtTS(p.time_open ? Date.parse(a.quote_at ?? p.time_open) : null)}>{priceStr(p.price_current, p.digits)}</td>
                          <td style={mTd('right', mono(p.sl ? C.b9 : C.dim2))} title={p.sl ? '' : 'No stop loss set'}>{priceStr(p.sl, p.digits)}</td>
                          <td style={mTd('right', mono(p.tp ? C.b9 : C.dim2))} title={p.tp ? '' : 'No take profit set'}>{priceStr(p.tp, p.digits)}</td>
                          <td style={mTd('right', mono(pnlColor(pl)))}>{fmtMoney(p.profit, a.currency, true)}</td>
                          <td style={mTd('right', mono((toNum(p.swap) ?? 0) >= 0 ? C.b9 : C.am))}>{fmtMoney(p.swap, a.currency, true)}</td>
                          <td style={mTd('left', mono(C.dm))} title={'Opened ' + fmtTS(Date.parse(p.time_open))}>{fmtAge(serverNow - Date.parse(p.time_open))}</td>
                          <td style={mTd('left', mono(C.dm))}>{a.magic}</td>
                          <td style={mTd('left', { color: C.dm })}>{a.strategy}</td>
                          <td style={mTd('left', mono(fc))} title={hbTitle(d.quoteAtMs, a.broker_tz_offset)}>{fresh}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

      {!res.loading && !hasLoadError && filtered.length === 0 && (
        <EmptyState title="No open positions" sub="Nothing is exposed to the market right now, or no positions match the current filter." />
      )}
    </section>
  );
}
